import React, { useEffect, useState } from 'react';
import { Alert, Button, Popconfirm, Space, Typography, message } from 'antd';
import { DeleteOutlined, KeyOutlined } from '@ant-design/icons';
import { liveChatApi } from '../../utils/api';

const COPY_TOOLTIPS = ['کپی', 'کپی شد'];

/**
 * کلید محرمانه API ویجت برای اتصال سرور به سرور.
 * جدا از فرم تنظیمات اعمال می‌شود (فوری). مقدار خام کلید فقط در state همین کامپوننت می‌ماند،
 * وارد widget والد نمی‌شود و با خروج از تب ویجت پاک می‌شود (تب‌ها forceRender هستند و unmount نمی‌شوند).
 */
const WidgetApiSetting = ({ widget, active, onChange }) => {
  const [loading, setLoading] = useState(null);
  const [rawKey, setRawKey] = useState(null);
  const hint = widget?.api_key_hint;

  useEffect(() => {
    if (!active) setRawKey(null);
  }, [active]);

  const generate = async () => {
    setLoading('generate');
    try {
      const res = await liveChatApi.generateWidgetApiKey();
      const { api_key: apiKey, ...info } = res?.data ?? {};
      onChange(info);
      setRawKey(apiKey ?? null);
      message.success('کلید API ساخته شد؛ همین حالا آن را کپی کنید');
    } catch (error) {
      message.error(error?.message || 'خطا در ساخت کلید API');
    } finally {
      setLoading(null);
    }
  };

  const revoke = async () => {
    setLoading('revoke');
    try {
      const res = await liveChatApi.revokeWidgetApiKey();
      if (res?.data) onChange(res.data);
      setRawKey(null);
      message.success('کلید API لغو شد');
    } catch (error) {
      message.error(error?.message || 'خطا در لغو کلید API');
    } finally {
      setLoading(null);
    }
  };

  // تا مشخص نشدن وضعیت کلید (widget خالی) دکمه‌ها غیرفعال‌اند تا ساخت کلید بدون تأیید، کلید موجود را جایگزین نکند
  const generateButton = (
    <Button
      icon={<KeyOutlined />}
      loading={loading === 'generate'}
      disabled={!widget || loading === 'revoke'}
      onClick={hint ? undefined : generate}
      style={{ fontFamily: 'CustomFont' }}
    >
      {hint ? 'ساخت کلید جدید' : 'ساخت کلید'}
    </Button>
  );

  return (
    <div className="livechat-panel p-4 mb-5">
      <div className="font-bold text-gray-800 dark:text-gray-100 mb-1 custom-font">Widget API (اتصال سرور به سرور)</div>
      <div className="text-xs text-gray-500 mb-4 custom-font">
        برای ساخت رابط گفتگوی اختصاصی از سرور خودتان، با ارسال کلید در هدر X-Api-Key. این کلید به همه گفتگوهای ویجت شرکت دسترسی دارد؛ آن را
        هرگز در کد مرورگر یا اپ موبایل قرار ندهید.
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 mb-4">
        <div>
          <div className="text-xs text-gray-500 mb-1 custom-font">آدرس پایه</div>
          <Typography.Text
            dir="ltr"
            copyable={widget?.api_base_url ? { text: widget.api_base_url, tooltips: COPY_TOOLTIPS } : false}
            className="text-xs livechat-embed-code"
            style={{ wordBreak: 'break-all' }}
          >
            {widget?.api_base_url || '—'}
          </Typography.Text>
        </div>
        <div>
          <div className="text-xs text-gray-500 mb-1 custom-font">کلید فعلی</div>
          {hint ? (
            <Typography.Text dir="ltr" className="text-xs livechat-embed-code">
              {hint}
            </Typography.Text>
          ) : (
            <span className="text-xs text-gray-400 custom-font">{widget ? 'کلیدی ساخته نشده است' : '—'}</span>
          )}
        </div>
      </div>

      {rawKey && (
        <Alert
          type="warning"
          showIcon
          className="custom-font mb-4"
          title="این کلید دیگر نمایش داده نمی‌شود؛ همین حالا آن را کپی و در جای امنی نگه دارید. با خروج از این تب یا صفحه پاک می‌شود."
          description={
            <div className="flex flex-col items-start gap-2 mt-2">
              <Typography.Paragraph dir="ltr" copyable={{ text: rawKey, tooltips: COPY_TOOLTIPS }} className="!mb-0 text-xs livechat-embed-code" style={{ wordBreak: 'break-all' }}>
                {rawKey}
              </Typography.Paragraph>
              <Button size="small" onClick={() => setRawKey(null)} style={{ fontFamily: 'CustomFont' }}>
                کلید را ذخیره کردم
              </Button>
            </div>
          }
        />
      )}

      <Space wrap>
        {hint ? (
          <Popconfirm
            title="کلید API عوض شود؟"
            description="کلید فعلی و همه اتصال‌هایی که از آن استفاده می‌کنند بلافاصله از کار می‌افتند."
            okText="بله"
            cancelText="خیر"
            onConfirm={generate}
          >
            {generateButton}
          </Popconfirm>
        ) : (
          generateButton
        )}
        {hint && (
          <Popconfirm
            title="کلید API لغو شود؟"
            description="API ویجت خاموش می‌شود و همه اتصال‌های فعلی بلافاصله از کار می‌افتند."
            okText="بله"
            cancelText="خیر"
            onConfirm={revoke}
          >
            <Button danger icon={<DeleteOutlined />} loading={loading === 'revoke'} disabled={loading === 'generate'} style={{ fontFamily: 'CustomFont' }}>
              لغو کلید
            </Button>
          </Popconfirm>
        )}
      </Space>
    </div>
  );
};

export default WidgetApiSetting;
